import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import '../styles.css';

function Subscribe() {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState(null);
  const [userTier, setUserTier] = useState(null); // <--- new
  const [checkingSession, setCheckingSession] = useState(true);

  const paymentSuccess = searchParams.get('reference');

  useEffect(() => {
    const checkSession = async () => {
      setCheckingSession(true);
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user?.id) {
        const id = sessionData.session.user.id;
        setUserId(id);
        // Fetch user profile to get tier
        const { data: profile } = await supabase
          .from('profiles')
          .select('subscription_tier')
          .eq('id', id)
          .single();
        setUserTier(profile?.subscription_tier || null);
      } else {
        setUserId(null);
        setUserTier(null);
      }
      setCheckingSession(false);
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (paymentSuccess && userId) {
      setSuccess('Thank you for supporting Choir Center! Redirecting...');
      setTimeout(() => navigate('/thank-you'), 1500);
    }
  }, [paymentSuccess, userId, navigate]);

  // Paystack checkout links
  const supporterLink = userId
    ? `https://paystack.shop/pay/choircentersupporter?callback_url=https://choircenter.com/thank-you?user_id=${userId}&tier=choir_supporter`
    : "/signup-supporter";
  const expertLink = userId
    ? `https://paystack.shop/pay/choircenterexpert?callback_url=https://choircenter.com/thank-you?user_id=${userId}&tier=choir_expert`
    : "/signup-expert";

  const checkIcon = (
    <svg className="w-4 h-4 text-green-600 mr-1 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-lime-50 via-white to-green-50 px-2 py-8">
      <div className="w-full max-w-5xl bg-white/95 rounded-3xl shadow-2xl p-4 md:p-8 border border-green-200">
        <h2 className="text-3xl md:text-4xl font-extrabold text-green-900 mb-3 text-center">
          Support Choir Center 🎶
        </h2>
        <p className="text-green-800 mb-8 text-center text-base md:text-lg">
          Your subscription keeps Choir Center online, pays for hosting, and helps us serve the choir community.<br />
          <span className="font-semibold text-green-700">Thank you for your support!</span>
        </p>
        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Choir Member */}
          <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-green-100 to-white border border-green-300 shadow-md p-6 md:p-8 transition-all duration-300 hover:scale-[1.03]">
            <span className="text-4xl mb-3">👤</span>
            <div className="font-bold text-green-800 text-xl mb-1">Choir Member</div>
            <span className="inline-block bg-green-200 text-green-900 text-xs px-3 py-0.5 rounded-full mb-2">
              Free Forever
            </span>
            <div className="text-green-700 text-lg mb-2">₦0 / month</div>
            <ul className="w-full max-w-xs mx-auto flex flex-col gap-2 mt-2 mb-5">
              <li className="flex items-center bg-green-50 border border-green-100 rounded-lg px-3 py-1 text-green-700 text-sm font-medium">
                {checkIcon}
                Access select choir sheet music & blog
              </li>
              <li className="flex items-center bg-green-50 border border-green-100 rounded-lg px-3 py-1 text-green-700 text-sm font-medium">
                {checkIcon}
                Connect with other choristers
              </li>
              <li className="flex items-center bg-lime-200/70 border border-lime-300 rounded-lg px-3 py-1 text-lime-800 text-sm font-semibold">
                <svg className="w-4 h-4 text-lime-700 mr-1" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" />
                  <text x="12" y="16" textAnchor="middle" fontSize="10" fill="currentColor">1</text>
                </svg>
                1 free download per month
              </li>
            </ul>
            {userId ? (
              <button
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black font-bold text-base shadow opacity-70 cursor-not-allowed"
                disabled
              >
                Already a Member
              </button>
            ) : (
              <Link
                to="/signup-member"
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black font-bold text-base shadow hover:scale-105 hover:from-green-600 hover:to-lime-300 transition-all"
              >
                Join Free
              </Link>
            )}
          </div>
          {/* Choir Supporter */}
          <div className="flex flex-col items-center relative rounded-2xl bg-gradient-to-br from-lime-50 to-green-100 border-2 border-lime-400 shadow-xl p-6 md:p-8 transition-all duration-300 hover:scale-[1.05]">
            <span className="absolute top-3 right-3 bg-gradient-to-br from-green-500 to-lime-300 text-white font-bold text-xs px-3 py-1 rounded-xl shadow">
              Most Popular
            </span>
            <span className="text-4xl mb-3">🎟️</span>
            <div className="font-bold text-green-900 text-xl mb-1">Choir Supporter</div>
            <span className="inline-block bg-green-300 text-green-900 text-xs px-3 py-0.5 rounded-full mb-2">
              Unlimited Access
            </span>
            <div className="text-green-700 text-lg mb-2">₦1,000 / month</div>
            <ul className="w-full max-w-xs mx-auto flex flex-col gap-2 mt-2 mb-5">
              <li className="flex items-center bg-green-50 border border-green-100 rounded-lg px-3 py-1 text-green-700 text-sm font-medium">
                {checkIcon}
                Unlimited PDF & Audio downloads (1 month)
              </li>
              <li className="flex items-center bg-green-50 border border-green-100 rounded-lg px-3 py-1 text-green-700 text-sm font-medium">
                {checkIcon}
                Priority access to new releases
              </li>
              <li className="flex items-center bg-lime-100 border border-lime-300 rounded-lg px-3 py-1 text-lime-800 text-sm font-semibold">
                {checkIcon}
                Supporter badge on your profile
              </li>
              <li className="flex items-center bg-lime-50 border border-lime-100 rounded-lg px-3 py-1 text-lime-800 text-sm font-semibold">
                {checkIcon}
                Help keep Choir Center running!
              </li>
            </ul>
            {!userId ? (
              <Link
                to="/signup-supporter"
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black font-bold text-base shadow hover:scale-105 hover:from-green-600 hover:to-lime-300 transition-all"
              >
                Subscribe as Supporter
              </Link>
            ) : userTier === 'choir_supporter' || userTier === 'choir_expert' ? (
              <button
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black font-bold text-base shadow opacity-70 cursor-not-allowed"
                disabled
              >
                Already Subscribed
              </button>
            ) : (
              <a
                href={supporterLink}
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-green-700 via-lime-400 to-green-400 text-black font-bold text-base shadow hover:scale-105 hover:from-green-600 hover:to-lime-300 transition-all text-center"
                rel="noopener noreferrer"
              >
                Upgrade to Supporter
              </a>
            )}
          </div>
          {/* Choir Expert */}
          <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-yellow-100 to-yellow-50 border-2 border-yellow-400 shadow-lg p-6 md:p-8 transition-all duration-300 hover:scale-[1.04]">
            <span className="text-4xl mb-3">🏅</span>
            <div className="font-bold text-yellow-800 text-xl mb-1">Choir Expert</div>
            <span className="inline-block bg-yellow-200 text-yellow-900 text-xs px-3 py-0.5 rounded-full mb-2">
              Premium
            </span>
            <div className="text-yellow-700 text-lg mb-2">₦7,500 / year</div>
            <ul className="w-full max-w-xs mx-auto flex flex-col gap-2 mt-2 mb-5">
              <li className="flex items-center bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1 text-yellow-900 text-sm font-medium">
                {checkIcon}
                All supporter benefits (for 1 year)
              </li>
              <li className="flex items-center bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1 text-yellow-900 text-sm font-medium">
                {checkIcon}
                Access to exclusive premium music books
              </li>
              <li className="flex items-center bg-yellow-100 border border-yellow-300 rounded-lg px-3 py-1 text-yellow-900 text-sm font-semibold">
                {checkIcon}
                Expert badge on your profile
              </li>
              <li className="flex items-center bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1 text-yellow-900 text-sm font-medium">
                {checkIcon}
                Direct support from Choir Center team
              </li>
              <li className="flex items-center bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1 text-yellow-900 text-sm font-medium">
                {checkIcon}
                Priority input on new features
              </li>
            </ul>
            {!userId ? (
              <Link
                to="/signup-expert"
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-300 text-yellow-900 font-bold text-base shadow hover:scale-105 hover:from-yellow-600 hover:to-yellow-200 transition-all"
              >
                Subscribe as Expert
              </Link>
            ) : userTier === 'choir_expert' ? (
              <button
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-300 text-yellow-900 font-bold text-base shadow opacity-70 cursor-not-allowed"
                disabled
              >
                Already Subscribed
              </button>
            ) : (
              <a
                href={expertLink}
                className="mt-auto px-6 py-2 rounded-full bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-300 text-yellow-900 font-bold text-base shadow hover:scale-105 hover:from-yellow-600 hover:to-yellow-200 transition-all text-center"
                rel="noopener noreferrer"
              >
                Upgrade to Expert
              </a>
            )}
          </div>
        </div>
        <div className="text-center mt-2 text-green-800 text-xs">
          <b>Note:</b> Subscription is <b>not</b> for selling choir music. It’s to keep Choir Center running for all. Thank you!
        </div>
        {error && (
          <div className="mt-4 text-red-700 text-sm text-center flex items-center justify-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
            </svg>
            {error}
            {!userId && (
              <Link
                to="/login"
                className="ml-2 underline text-green-700 hover:text-green-900"
              >
                Log in
              </Link>
            )}
          </div>
        )}
        {success && <div className="mt-4 text-green-700 text-sm text-center">{success}</div>}
        <div className="mt-6 text-center text-green-700">
          Already subscribed?{" "}
          <Link to="/profile" className="text-green-800 underline">View your profile</Link>
        </div>
      </div>
    </div>
  );
}

export default Subscribe;
